import React, { useMemo } from 'react'
import { motion } from 'motion/react'

import { DotData, EffectBackgroundProps } from '../types'

export const DotMatrixEffect = React.memo(({ layerStyle, index, values }: EffectBackgroundProps) => {
  const effectColors = useMemo(() => values.effectColors || ['#6366f1'], [values.effectColors])
  const effectSpeed = values.effectSpeed ?? 100
  const effectOpacity = values.effectOpacity ?? 100
  const effectBackgroundColor = values.effectBackgroundColor || '#000000'
  const opacityValue = effectOpacity / 100

  const dotSize = 4
  const dotColor = effectColors[0] || '#6366f1'

  const dotMatrixDots = useMemo(() => {
    const gridSpacing = 5
    const cols = Math.ceil(100 / gridSpacing) + 1
    const rows = Math.ceil(100 / gridSpacing) + 1
    const dotsArray: Array<DotData> = []

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        dotsArray.push({
          id: `${row}-${col}`,
          x: col * gridSpacing,
          y: row * gridSpacing,
          col,
          row,
        })
      }
    }
    return dotsArray
  }, [])

  return (
    <div
      style={{
        position: 'absolute' as const,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -index - 1,
        overflow: 'hidden',
        backgroundColor: effectBackgroundColor,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
        }}
      >
        {dotMatrixDots.map((dot) => {
          const waveDelay = (Math.sin(dot.col * 0.3) + Math.cos(dot.row * 0.3)) * 0.5
          const diagonalDelay = (dot.col + dot.row) * 0.03

          return (
            <motion.div
              key={dot.id}
              style={{
                position: 'absolute',
                width: `${dotSize}px`,
                height: `${dotSize}px`,
                left: `${dot.x}%`,
                top: `${dot.y}%`,
                backgroundColor: dotColor,
                boxShadow: `0 0 ${dotSize * 2}px ${dotColor}40`,
                borderRadius: '50%',
              }}
              animate={{
                scale: [1, 2, 1],
                opacity: [opacityValue, opacityValue / 2, opacityValue],
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4 * (100 / Math.max(effectSpeed, 1)),
                repeat: Infinity,
                ease: 'easeInOut',
                delay: waveDelay + diagonalDelay,
              }}
            />
          )
        })}
      </div>

      {/* Animated wave overlay */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at center, transparent 0%, ${effectBackgroundColor}80 100%)`,
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 5 * (100 / Math.max(effectSpeed, 1)),
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </div>
  )
})
DotMatrixEffect.displayName = 'DotMatrixEffect'
